import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Monitoring.css';
import { getStoredAuthClaims, getStoredAuthToken, isAdminUser } from '../utils/auth';
import { readDeviceTelemetryStore, subscribeDeviceTelemetry } from '../utils/deviceTelemetry';
import { createRelayViewer } from '../utils/streamRelay';

function Monitoring() {
  const authClaims = getStoredAuthClaims();
  const isAdmin = isAdminUser(authClaims);
  const userCustomerId = Number(authClaims?.customer_id || 0);

  const [customers, setCustomers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedSerialNumber, setSelectedSerialNumber] = useState('');
  const [liveDevices, setLiveDevices] = useState(() => readDeviceTelemetryStore());
  const [remoteLiveDevices, setRemoteLiveDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [liveWatching, setLiveWatching] = useState(false);
  const [liveStatus, setLiveStatus] = useState('');
  const liveViewerRef = useRef(null);
  const liveVideoRef = useRef(null);

  const getApiBaseUrl = () => process.env.REACT_APP_API_BASE_URL || '/api/v1';

  const parseJsonResponse = async (response) => {
    const contentType = response.headers.get('content-type') || '';

    if (!contentType.includes('application/json')) {
      const text = await response.text();
      throw new Error(text.slice(0, 120) || 'Expected JSON response');
    }

    return response.json();
  };

  const fetchWithAuth = async (url) => {
    const token = getStoredAuthToken();

    if (!token) {
      throw new Error('Sign in to view monitoring data.');
    }

    return fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  };

  const loadMonitoringData = async () => {
    setLoading(true);
    setError('');

    try {
      const customersResponse = await fetchWithAuth(`${getApiBaseUrl()}/customers`);
      const customersData = await parseJsonResponse(customersResponse);

      if (!customersResponse.ok) {
        throw new Error(customersData.error || 'Failed to fetch customers');
      }

      const allCustomerRows = customersData.customers || [];
      const customerRows = isAdmin
        ? allCustomerRows
        : allCustomerRows.filter((customer) => Number(customer.id) === userCustomerId);
      setCustomers(customerRows);

      const bucketResponses = await Promise.all(
        customerRows.map(async (customer) => {
          const response = await fetchWithAuth(`${getApiBaseUrl()}/buckets/by-customer/${customer.id}`);
          const data = await parseJsonResponse(response);

          if (!response.ok) {
            throw new Error(data.error || `Failed to fetch buckets for ${customer.name || customer.id}`);
          }

          return {
            customer,
            buckets: data.buckets || []
          };
        })
      );

      const deviceRows = bucketResponses.flatMap(({ customer, buckets }) =>
        buckets.map((bucket) => ({
          deviceKey: bucket.id,
          customerId: customer.id,
          customerName: customer.name || `Customer ${customer.id}`,
          customerEmail: customer.email || '',
          organizationCode: customer.organization_code || '',
          bucketId: bucket.id,
          serialNumber: bucket.serial_number,
          bucketType: bucket.bucket_type,
          bucketStatus: bucket.status || 'Available'
        }))
      );

      const sortedDevices = deviceRows.sort((left, right) => {
        const leftConnected = liveDevices.some((device) => device.serialNumber === left.serialNumber && device.connected);
        const rightConnected = liveDevices.some((device) => device.serialNumber === right.serialNumber && device.connected);

        if (leftConnected !== rightConnected) {
          return leftConnected ? -1 : 1;
        }

        return String(left.serialNumber).localeCompare(String(right.serialNumber));
      });

      setDevices(sortedDevices);

      setSelectedSerialNumber((currentValue) => {
        if (currentValue && sortedDevices.some((device) => device.serialNumber === currentValue)) {
          return currentValue;
        }

        const firstConnectedDevice = sortedDevices.find((device) =>
          liveDevices.some((liveDevice) => liveDevice.serialNumber === device.serialNumber && liveDevice.connected)
        );

        return firstConnectedDevice?.serialNumber || sortedDevices[0]?.serialNumber || '';
      });
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const loadRemoteLiveTelemetry = async () => {
    try {
      const response = await fetchWithAuth(`${getApiBaseUrl()}/telemetry/live?maxAgeSeconds=45`);
      const data = await parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch live telemetry');
      }

      const nextRemoteDevices = Array.isArray(data.devices)
        ? data.devices.map((device) => ({
          serialNumber: device.serial_number,
          deviceName: device.device_name || device.serial_number,
          customerId: Number(device.customer_id || 0),
          organizationCode: device.organization_code || '',
          connected: Boolean(device.connected),
          status: device.status || '',
          telemetry: device.telemetry || '',
          cameraUrl: device.camera_url || device.cameraUrl || '',
          parsedTelemetry: {
            temperature: device.temperature || '',
            pressure: device.pressure || ''
          },
          readCount: Number(device.read_count || 0),
          updatedAt: device.updated_at || new Date().toISOString()
        }))
        : [];

      setRemoteLiveDevices(nextRemoteDevices);
    } catch (remoteError) {
      // Monitoring still works with local telemetry even when remote feed is unavailable.
    }
  };

  useEffect(() => {
    loadMonitoringData();
    loadRemoteLiveTelemetry();

    const unsubscribe = subscribeDeviceTelemetry((nextDevices) => {
      setLiveDevices(nextDevices);
    });

    const pollTimer = setInterval(() => {
      loadRemoteLiveTelemetry();
    }, 3000);

    return () => {
      unsubscribe();
      clearInterval(pollTimer);
    };
  }, []);

  useEffect(() => {
    if (!selectedSerialNumber && devices.length > 0) {
      setSelectedSerialNumber(devices[0].serialNumber);
    }
  }, [devices, selectedSerialNumber]);

  const visibleSerialNumbers = useMemo(
    () => new Set(devices.map((device) => device.serialNumber)),
    [devices]
  );

  const mergedLiveDevices = useMemo(() => {
    const mergedMap = new Map();

    [...liveDevices, ...remoteLiveDevices].forEach((device) => {
      if (!device?.serialNumber) {
        return;
      }

      const currentDevice = mergedMap.get(device.serialNumber);
      if (!currentDevice) {
        mergedMap.set(device.serialNumber, device);
        return;
      }

      const currentTimestamp = new Date(currentDevice.updatedAt || 0).getTime();
      const nextTimestamp = new Date(device.updatedAt || 0).getTime();
      if (nextTimestamp >= currentTimestamp) {
        mergedMap.set(device.serialNumber, device);
      }
    });

    return Array.from(mergedMap.values());
  }, [liveDevices, remoteLiveDevices]);

  const scopedLiveDevices = useMemo(
    () => mergedLiveDevices.filter((device) => visibleSerialNumbers.has(device.serialNumber)),
    [mergedLiveDevices, visibleSerialNumbers]
  );

  const liveDeviceMap = useMemo(
    () => new Map(scopedLiveDevices.map((device) => [device.serialNumber, device])),
    [scopedLiveDevices]
  );

  const connectedDevices = useMemo(
    () => devices.filter((device) => liveDeviceMap.get(device.serialNumber)?.connected),
    [devices, liveDeviceMap]
  );

  const selectedDevice = useMemo(
    () => devices.find((device) => device.serialNumber === selectedSerialNumber) || null,
    [devices, selectedSerialNumber]
  );

  const selectedTelemetry = selectedDevice ? liveDeviceMap.get(selectedDevice.serialNumber) || null : null;

  const totalLiveUpdates = scopedLiveDevices.reduce((total, device) => total + Number(device.readCount || 0), 0);

  const stopWatching = () => {
    liveViewerRef.current?.stop();
    liveViewerRef.current = null;
    if (liveVideoRef.current) {
      liveVideoRef.current.srcObject = null;
    }
    setLiveWatching(false);
    setLiveStatus('');
  };

  const startWatching = () => {
    if (!selectedDevice || typeof RTCPeerConnection === 'undefined') {
      setLiveStatus('This browser cannot play live video.');
      return;
    }

    stopWatching();
    setLiveWatching(true);
    liveViewerRef.current = createRelayViewer({
      serialNumber: selectedDevice.serialNumber,
      customerId: selectedDevice.customerId,
      onStatus: setLiveStatus,
      onStream: (stream) => {
        if (liveVideoRef.current) {
          liveVideoRef.current.srcObject = stream;
        }
      }
    });
  };

  useEffect(() => {
    stopWatching();
    return () => {
      liveViewerRef.current?.stop();
      liveViewerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSerialNumber]);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Monitoring</h1>
        <p>
          {isAdmin
            ? 'Track connected BT devices across all organizations and inspect live telemetry coming from Connect sessions, including phones.'
            : 'Track connected BT devices in your organization and inspect live telemetry coming from Connect sessions, including phones.'}
        </p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h4>Total Customers</h4>
          <p className="stat-value">{customers.length}</p>
        </div>
        <div className="stat-card">
          <h4>Total Devices</h4>
          <p className="stat-value">{devices.length}</p>
        </div>
        <div className="stat-card">
          <h4>Connected Now</h4>
          <p className="stat-value">{connectedDevices.length}</p>
        </div>
        <div className="stat-card">
          <h4>Live Updates</h4>
          <p className="stat-value">{totalLiveUpdates}</p>
        </div>
        <div className="stat-card">
          <h4>System Status</h4>
          <p className="stat-value status">✓ Online</p>
        </div>
      </div>

      <div className="monitoring-grid">
        <div className="card">
          <div className="card-header">
            <div>
              <h2>BT Devices</h2>
              <p className="card-subtitle">Connected devices are listed first. Select one to inspect its live data.</p>
            </div>
            <button className="btn-refresh" onClick={loadMonitoringData} disabled={loading}>
              ↻ Refresh
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          {loading && <div className="loading">Loading devices...</div>}

          {!loading && devices.length === 0 && (
            <div className="empty-state">
              <p>No BT devices found</p>
              <small>Add buckets in Manage and connect to a device in Connect.</small>
            </div>
          )}

          {!loading && devices.length > 0 && (
            <div className="device-list">
              {devices.map((device) => {
                const liveDevice = liveDeviceMap.get(device.serialNumber);
                const isSelected = selectedSerialNumber === device.serialNumber;
                const isConnected = Boolean(liveDevice?.connected);

                return (
                  <button
                    key={device.deviceKey}
                    type="button"
                    className={`device-item ${isSelected ? 'selected' : ''} ${isConnected ? 'connected' : ''}`}
                    onClick={() => setSelectedSerialNumber(device.serialNumber)}
                  >
                    <div className="device-item-main">
                      <div className="device-item-title-row">
                        <h4>{device.serialNumber}</h4>
                        <span className={`status-pill ${isConnected ? 'connected' : 'idle'}`}>
                          {isConnected ? 'Connected' : 'Idle'}
                        </span>
                      </div>
                      <p>{device.bucketType} • {device.customerName}</p>
                      <small>{device.customerEmail || device.organizationCode || `Bucket ID ${device.bucketId}`}</small>
                    </div>
                    <span className="arrow">→</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="card">
          <h2>Live Device Data</h2>

          {!selectedDevice && (
            <div className="empty-state">
              <p>Select a device to see live telemetry</p>
            </div>
          )}

          {selectedDevice && (
            <div className="device-detail-panel">
              <div className="device-detail-header">
                <div>
                  <h3>{selectedDevice.serialNumber}</h3>
                  <p>{selectedDevice.bucketType} • {selectedDevice.customerName}</p>
                </div>
                <span className={`status-pill ${selectedTelemetry?.connected ? 'connected' : 'idle'}`}>
                  {selectedTelemetry?.connected ? 'Live' : 'No live connection'}
                </span>
              </div>

              <div className="device-meta-grid">
                <div>
                  <span className="meta-label">Customer</span>
                  <strong>{selectedDevice.customerName}</strong>
                </div>
                <div>
                  <span className="meta-label">Organization</span>
                  <strong>{selectedDevice.organizationCode || '—'}</strong>
                </div>
                <div>
                  <span className="meta-label">Bucket type</span>
                  <strong>{selectedDevice.bucketType}</strong>
                </div>
                <div>
                  <span className="meta-label">Bucket status</span>
                  <strong>{selectedDevice.bucketStatus}</strong>
                </div>
              </div>

              <div className="telemetry-panel">
                <div className="telemetry-panel-header">
                  <h4>Telemetry Snapshot</h4>
                  <span>{selectedTelemetry?.updatedAt ? `Updated ${new Date(selectedTelemetry.updatedAt).toLocaleTimeString()}` : 'Waiting for live data'}</span>
                </div>

                <div className="telemetry-grid">
                  <div className="telemetry-card">
                    <span className="telemetry-label">Temperature</span>
                    <strong>{selectedTelemetry?.parsedTelemetry?.temperature || '--'}</strong>
                  </div>
                  <div className="telemetry-card">
                    <span className="telemetry-label">Pressure</span>
                    <strong>{selectedTelemetry?.parsedTelemetry?.pressure || '--'}</strong>
                  </div>
                  <div className="telemetry-card">
                    <span className="telemetry-label">Notifications</span>
                    <strong>{selectedTelemetry?.readCount ?? 0}</strong>
                  </div>
                  <div className="telemetry-card">
                    <span className="telemetry-label">Connection</span>
                    <strong>{selectedTelemetry?.status || 'Not connected'}</strong>
                  </div>
                </div>

                {(selectedTelemetry?.cameraSnapshotData || selectedTelemetry?.cameraUrl) && (
                  <div className="camera-preview-panel">
                    <span className="telemetry-label">Camera preview</span>
                    <img
                      className="monitoring-camera-preview"
                      src={selectedTelemetry.cameraSnapshotData || selectedTelemetry.cameraUrl}
                      alt={`${selectedDevice.serialNumber} camera preview`}
                    />
                  </div>
                )}

                <div className="camera-preview-panel">
                  <span className="telemetry-label">Live video</span>
                  <video
                    ref={liveVideoRef}
                    className="monitoring-camera-preview monitoring-live-video"
                    autoPlay
                    muted
                    playsInline
                    hidden={!liveWatching}
                  />
                  <div className="live-video-actions">
                    <button
                      type="button"
                      className="btn-refresh"
                      onClick={liveWatching ? stopWatching : startWatching}
                    >
                      {liveWatching ? 'Stop watching' : 'Watch live'}
                    </button>
                    {liveStatus && <span className="live-video-status">{liveStatus}</span>}
                  </div>
                </div>

                {selectedTelemetry?.recordingUrl && (
                  <div className="camera-preview-panel">
                    <span className="telemetry-label">Recorded clip</span>
                    <video
                      className="monitoring-camera-preview"
                      src={selectedTelemetry.recordingUrl}
                      controls
                      playsInline
                    />
                  </div>
                )}

                <div className="telemetry-raw">
                  <span className="telemetry-label">Raw payload</span>
                  <pre>{selectedTelemetry?.telemetry || 'Waiting for telemetry payload...'}</pre>
                </div>
              </div>

              {!selectedTelemetry && (
                <div className="empty-state compact-state">
                  <p>No live telemetry is available for this device yet</p>
                  <small>Connect to the same serial number in Connect on phone or desktop to stream data here.</small>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Monitoring;